import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, LOCALE_ID } from '@angular/core';
import { Router } from '@angular/router';
import { AuthActions } from '@domgen/dgx-fe-authentication';
import { filterNullUndefined } from '@domgen/dgx-fe-common';
import { GAService } from '@domgen/dgx-fe-ga';
import { LocaleId } from '@domgen/dgx-fe-i18n';
import { ErrorRoutes } from '@error/util-models';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import {
  QuoteResponse,
  RegisteredItem,
  RegisteredItemProductDetails,
  RegistrationApplianceDetails,
  RegistrationRequest,
  RegistrationResponse,
  RegistrationRoutes,
  RegistrationSource,
  RegistrationSystemCode,
  RegistriaUserRequest,
  RegistriaUsersResponse,
} from '@us-registration/util-models';
import posthog from 'posthog-js';
import { of, tap } from 'rxjs';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';
import { RegistrationService } from '../services/registration.service';
import {
  formatRegistrationStoredTags,
  formatRegistrationStoredTagsNoOffer,
} from '../utils/analytics/analytics.utils';
import {
  determineRegistrationSourceFromRegistrationSystemCode,
  formatRegistriaRequest,
} from '../utils/registria/registria.utils';
import * as RegistrationActions from './registration.actions';
import * as RegistrationSelectors from './registration.selectors';

@Injectable()
export class RegistrationEffects {
  getQuoteRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegistrationActions.getQuoteRequest),
      map(
        ({
          registrationRequest,
        }: {
          registrationRequest: RegistrationRequest;
        }) => registrationRequest
      ),
      map((registrationRequest: RegistrationRequest) =>
        RegistrationActions.getQuote({ registrationRequest })
      )
    )
  );

  getQuote$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegistrationActions.getQuote),
      switchMap((action: { registrationRequest: RegistrationRequest }) =>
        this.registrationService.getQuote(action.registrationRequest).pipe(
          map((quoteResponse: QuoteResponse) =>
            RegistrationActions.getQuoteSuccess({ quoteResponse })
          ),
          catchError((error) => {
            switch (error.status) {
              case 204:
              case 503:
                return of(
                  RegistrationActions.getQuoteNoContent({
                    statusCode: error.status,
                  })
                );
              default:
                return of(
                  RegistrationActions.getQuoteFailure({
                    statusCode: error.status,
                  })
                );
            }
          })
        )
      )
    )
  );

  registerApplianceRequest$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegistrationActions.registerApplianceRequest),
      map(
        ({
          registrationRequest,
        }: {
          registrationRequest: RegistrationRequest;
        }) => registrationRequest
      ),
      map((registrationRequest: RegistrationRequest) =>
        RegistrationActions.registerAppliance({ registrationRequest })
      )
    )
  );

  registerAppliance$ = createEffect(() =>
    this.actions$.pipe(
      ofType(RegistrationActions.registerAppliance),
      switchMap((action: { registrationRequest: RegistrationRequest }) =>
        this.registrationService
          .registerAppliance(action.registrationRequest)
          .pipe(
            map((registrationResponse: RegistrationResponse) =>
              RegistrationActions.registerApplianceSuccess({
                registrationResponse,
              })
            ),
            catchError((error: Error) =>
              of(RegistrationActions.registerApplianceFailure({ error }))
            )
          )
      )
    )
  );

  trackRegisterApplianceSuccess$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RegistrationActions.registerApplianceSuccess),
        withLatestFrom(
          this.store.select(
            RegistrationSelectors.selectRegistrationCustomerDetails
          ),
          this.store.select(RegistrationSelectors.selectRegistrationRequest)
        ),
        tap(([action, customerDetails, registrationRequest]) => {
          const registeredItem: RegisteredItem = action.registrationResponse[0];
          const codeUsed = localStorage.getItem('registrationCodeUsed');

          if (
            !('products' in registeredItem) ||
            !('appliances' in registeredItem)
          ) {
            this.gaService.tag(
              formatRegistrationStoredTagsNoOffer(
                registrationRequest as RegistrationRequest,
                codeUsed
              )
            );
          }

          if (
            registeredItem.products &&
            registeredItem.appliances &&
            customerDetails
          ) {
            const product: RegisteredItemProductDetails =
              registeredItem.products[0];
            const appliance: RegistrationApplianceDetails =
              registeredItem.appliances[0];

            this.gaService.tag({
              event: 'generateOffer',
              eventCategory: 'applianceOffer',
              eventAction: 'offerGenerated',
              eventLabel: product.price,
              applianceName: appliance.type,
              appliancePurchaseCost: appliance.purchasePrice,
              appliancePurchaseDate: appliance.purchaseDate,
              appliancePrice: product.price,
              pagename: 'protection-details',
              quoteId: registeredItem.Id,
            });

            this.gaService.tag(
              formatRegistrationStoredTags(
                customerDetails,
                registeredItem,
                codeUsed
              )
            );

            localStorage.setItem('hasTrackedGAPurchaseEvent', 'false');
          }
        })
      ),
    { dispatch: false }
  );

  setQuoteOutOfGuaranteeToLocalStorage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RegistrationActions.registerApplianceSuccess),
        withLatestFrom(
          this.store.select(
            RegistrationSelectors.selectRegisteredItemOutOfGuarantee
          )
        ),
        tap(([_, registeredItemOutOfGuarantee]) =>
          localStorage.setItem(
            'quoteOutOfGuarantee',
            registeredItemOutOfGuarantee.toString()
          )
        )
      ),
    { dispatch: false }
  );

  getRegistriaUserResponse$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RegistrationActions.registerApplianceSuccess),
        withLatestFrom(
          this.store.select(RegistrationSelectors.selectRegistriaUserRequest)
        ),
        filterNullUndefined(),
        map(([action, registriaUserRequest]) => {
          let appliance: RegistrationApplianceDetails | undefined;
          const registrationSystemCode: RegistrationSystemCode =
            registriaUserRequest.registrationSystemCode;
          const source: RegistrationSource =
            determineRegistrationSourceFromRegistrationSystemCode(
              registrationSystemCode
            );

          if (!registriaUserRequest.registrationRequest) {
            return undefined;
          }

          if (!registriaUserRequest.hasRegisteredItemQuoteNumber) {
            appliance = registriaUserRequest.registrationRequest.merchandise[0];
          } else {
            appliance = action.registrationResponse[0].appliances[0];
          }

          return formatRegistriaRequest(
            source,
            registriaUserRequest.registrationCustomerDetails,
            appliance,
            registriaUserRequest.userToken,
            navigator.language ?? this.localeId
          );
        }),
        filterNullUndefined(),
        switchMap((registriaRequest: RegistriaUserRequest) =>
          this.registrationService.registriaUsers(registriaRequest)
        ),
        tap((registriaUserResponse: RegistriaUsersResponse) => {
          localStorage.removeItem('registriaUserResponse');

          this.store.dispatch(
            RegistrationActions.addRegistriaUserResponse({
              registriaUserResponse,
            })
          );
        })
      ),
    { dispatch: false }
  );

  getRegistriaUserResponseWib$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(
          RegistrationActions.getQuoteSuccess,
          RegistrationActions.getQuoteNoContent
        ),
        withLatestFrom(
          this.store.select(RegistrationSelectors.selectRegistriaUserRequestWib)
        ),
        filterNullUndefined(),
        map(([action, registriaUserRequest]) => {
          const registrationSystemCode: RegistrationSystemCode =
            registriaUserRequest.registrationSystemCode;

          const source: RegistrationSource =
            determineRegistrationSourceFromRegistrationSystemCode(
              registrationSystemCode
            );
          let quoteSuccessAction: QuoteResponse | undefined;
          let quoteNoContentAction: number | undefined;

          if ('quoteResponse' in action) {
            quoteSuccessAction = action.quoteResponse;
          }
          if ('statusCode' in action) {
            quoteNoContentAction = action.statusCode;
          }

          if (
            (quoteSuccessAction === undefined &&
              quoteNoContentAction === undefined) ||
            (quoteNoContentAction !== undefined &&
              quoteNoContentAction !== 204 &&
              quoteNoContentAction !== 503)
          ) {
            return undefined;
          }

          if (!registriaUserRequest.registrationRequest) {
            return undefined;
          }

          const appliance: RegistrationApplianceDetails | undefined =
            registriaUserRequest.registrationRequest.merchandise[0];

          return formatRegistriaRequest(
            source,
            registriaUserRequest.registrationCustomerDetails,
            appliance,
            registriaUserRequest.userToken
          );
        }),
        filterNullUndefined(),
        switchMap((registriaRequest: RegistriaUserRequest) => {
          return this.registrationService.registriaUsers(registriaRequest);
        }),
        tap((registriaUserResponse: RegistriaUsersResponse) => {
          localStorage.removeItem('registriaUserResponse');

          this.store.dispatch(
            RegistrationActions.addRegistriaUserResponse({
              registriaUserResponse,
            })
          );
        })
      ),
    { dispatch: false }
  );

  expiredSession$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(AuthActions.sessionExpired),
        tap(() => this.document.location.reload())
      ),
    { dispatch: false }
  );

  registerAppliancesFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RegistrationActions.registerApplianceFailure),
        tap(() => {
          posthog.capture('Quote_Internal_Error');
          this.router.navigateByUrl(ErrorRoutes.Generic);
        })
      ),
    { dispatch: false }
  );

  quoteFailure$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RegistrationActions.getQuoteFailure),
        tap((action) => {
          if (action.statusCode === 502) {
            posthog.capture('registration-failed');
            this.router.navigateByUrl(ErrorRoutes.Generic);
          }
          posthog.capture('Quote_Internal_Error');
          this.router.navigateByUrl(ErrorRoutes.Generic);
        })
      ),
    { dispatch: false }
  );

  quoteNoContent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(RegistrationActions.getQuoteNoContent),
        tap((action) => {
          if (action.statusCode === 204 || action.statusCode === 503) {
            posthog.capture('Quote_Internal_Error');
            this.router.navigateByUrl(
              RegistrationRoutes.RegistrationConfirmation
            );
          }
        })
      ),
    { dispatch: false }
  );

  constructor(
    @Inject(DOCUMENT) private readonly document: Document,
    @Inject(LOCALE_ID) private readonly localeId: LocaleId,
    private readonly actions$: Actions,
    private readonly registrationService: RegistrationService,
    private readonly store: Store,
    private readonly gaService: GAService,
    private readonly router: Router
  ) {}
}
